/**
 * instagram-reels.js
 * Lazy-attaches <source> from JSON + data-reel-primary-src; play/pause on tap/click/keyboard.
 * Optional: ?ir_debug=1 or localStorage ir_debug=1 for console logs.
 */
(function () {
  if (typeof document === 'undefined') return;

  const debug =
    typeof window !== 'undefined' &&
    (window.__IR_DEBUG__ === true ||
      window.localStorage?.getItem('ir_debug') === '1' ||
      window.sessionStorage?.getItem('ir_debug') === '1' ||
      /[?&#]ir_debug=1(?:[&]|$)/.test((window.location?.search || '') + (window.location?.hash || '')) ||
      (typeof window.Shopify !== 'undefined' && window.Shopify.designMode));

  const log = debug
    ? (...a) => {
        console.log('[instagram-reels]', ...a);
      }
    : () => {};

  const pickUrl = (s) => {
    if (!s || typeof s !== 'object') return '';
    const u = s.url ?? s.URL ?? s.src ?? s.href ?? s.playback_url ?? s.playbackUrl;
    return typeof u === 'string' && u.trim() ? u.trim() : '';
  };

  const sourceRank = (s) => {
    if (!pickUrl(s)) return 99;
    const fmt = String(s.format || '').toLowerCase();
    const mime = String(s.mime_type || '').toLowerCase();
    if (fmt === 'm3u8' || mime.includes('mpegurl') || mime.includes('m3u8')) return 2;
    if (fmt === 'mp4' || mime === 'video/mp4' || fmt === 'mov' || mime.includes('quicktime')) return 0;
    return 1;
  };

  const sortSources = (arr) => [...arr].sort((a, b) => sourceRank(a) - sourceRank(b));

  const readSources = (video) => {
    const frame = video.closest('.instagram-reels__frame');
    const scope = frame || video.parentElement;
    if (!scope) return [];
    const srcEl = scope.querySelector('script.instagram-reels__sources');
    if (!srcEl) {
      try {
        return JSON.parse(video.dataset.sources || '[]');
      } catch (e) {
        return [];
      }
    }
    try {
      return JSON.parse((srcEl.textContent || '').trim() || '[]');
    } catch (e) {
      return [];
    }
  };

  /** Returns playable source objects (mp4 before m3u8); falls back to data-reel-primary-src. */
  const buildSourceList = (video) => {
    const fb = video.dataset.reelPrimarySrc || '';
    const raw = readSources(video);
    if (!Array.isArray(raw) || !raw.length) {
      return fb ? [{ format: 'mp4', mime_type: 'video/mp4', url: fb }] : [];
    }
    const normalized = raw
      .map((s) => {
        const url = pickUrl(s);
        return url ? { ...s, url } : null;
      })
      .filter(Boolean);
    let list = sortSources(normalized);
    if (!list.length && fb) {
      list = [{ format: 'mp4', mime_type: 'video/mp4', url: fb }];
    }
    return list;
  };

  const attachSources = (video) => {
    if (!video || video.dataset.attached === 'true') return;

    const list = buildSourceList(video);
    if (!list.length) return;

    let n = 0;
    for (let i = 0; i < list.length; i += 1) {
      const s = list[i];
      const url = pickUrl(s);
      if (!url) continue;
      const el = document.createElement('source');
      el.src = url;
      el.type = s.mime_type || (s.format ? 'video/' + s.format : 'video/mp4');
      video.appendChild(el);
      n += 1;
    }
    if (n === 0 && video.dataset.reelPrimarySrc) {
      const el = document.createElement('source');
      el.src = video.dataset.reelPrimarySrc;
      el.type = 'video/mp4';
      video.appendChild(el);
      n = 1;
    }
    if (n === 0) return;

    video.muted = true;
    video.loop = true;
    video.addEventListener('ended', () => {
      try {
        video.currentTime = 0;
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (e) {
        /* ignore */
      }
    });

    video.load();
    video.dataset.attached = 'true';
    log('attachSources', n, 'source(s)');
  };

  const syncPlayHit = (playHit, video) => {
    const playLabel = playHit.dataset.ariaPlay || '';
    const pauseLabel = playHit.dataset.ariaPause || '';
    const playing = !video.paused;
    playHit.setAttribute('aria-pressed', playing ? 'true' : 'false');
    playHit.setAttribute('aria-label', playing ? pauseLabel : playLabel);
  };

  const HAVE_CURRENT_DATA =
    typeof HTMLMediaElement !== 'undefined' && HTMLMediaElement.HAVE_CURRENT_DATA !== undefined
      ? HTMLMediaElement.HAVE_CURRENT_DATA
      : 2;

  const initVideoTile = (tile) => {
    if (tile.dataset.irBound === 'true') return;

    const playHit = tile.querySelector('.instagram-reels__play-hit');
    const video = playHit?.querySelector('video.instagram-reels__video');
    if (!playHit || !video) return;

    const playWhenReady = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          const retry = () => {
            const p2 = video.play();
            if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
          };
          video.addEventListener('canplay', retry, { once: true });
          video.addEventListener('loadeddata', retry, { once: true });
        });
      }
    };

    const safePlay = () => {
      if (video.readyState >= HAVE_CURRENT_DATA) {
        playWhenReady();
        return;
      }
      let ran = false;
      const once = () => {
        if (ran) return;
        ran = true;
        playWhenReady();
      };
      video.addEventListener('canplay', once, { once: true });
      video.addEventListener('loadeddata', once, { once: true });
      if (video.readyState >= HAVE_CURRENT_DATA) once();
    };

    const togglePlayback = () => {
      attachSources(video);
      if (
        video.dataset.attached === 'true' &&
        video.readyState === 0 &&
        !video.currentSrc &&
        video.networkState === 0 &&
        video.querySelectorAll('source').length > 0
      ) {
        video.querySelectorAll('source').forEach((el) => el.remove());
        delete video.dataset.attached;
        attachSources(video);
      }
      video.muted = true;
      if (video.paused) {
        safePlay();
      } else {
        video.pause();
      }
    };

    const TAP = 14;
    let touchTapSince = -Infinity;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchTracking = false;

    playHit.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) {
          touchTracking = false;
          return;
        }
        touchTracking = true;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      },
      { passive: true }
    );

    playHit.addEventListener('touchcancel', () => {
      touchTracking = false;
    });

    playHit.addEventListener(
      'touchend',
      (e) => {
        if (!touchTracking || e.changedTouches.length !== 1) return;
        touchTracking = false;
        const t = e.changedTouches[0];
        if (Math.abs(t.clientX - touchStartX) > TAP || Math.abs(t.clientY - touchStartY) > TAP) return;
        touchTapSince = Date.now();
        togglePlayback();
      },
      { passive: true }
    );

    playHit.addEventListener(
      'click',
      (e) => {
        if (Date.now() - touchTapSince < 700) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        togglePlayback();
      },
      true
    );

    video.addEventListener('play', () => syncPlayHit(playHit, video));
    video.addEventListener('pause', () => syncPlayHit(playHit, video));
    syncPlayHit(playHit, video);

    if (!('IntersectionObserver' in window)) {
      tile.dataset.irBound = 'true';
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== tile) return;
          const v = tile.querySelector('video.instagram-reels__video');
          if (!v) return;
          if (entry.isIntersecting) {
            attachSources(v);
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { rootMargin: '400px 0px', threshold: 0 }
    );

    tile._irIo = io;
    io.observe(tile);
    tile.dataset.irBound = 'true';
  };

  const initIn = (root) => {
    if (!root?.querySelectorAll) return;
    root.querySelectorAll('.instagram-reels__tile').forEach((t) => initVideoTile(t));
  };

  initIn(document);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (root instanceof Element && root.querySelector('.instagram-reels__tile')) initIn(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target;
    if (!(root instanceof Element)) return;
    root.querySelectorAll('.instagram-reels__tile').forEach((tile) => {
      if (tile._irIo) {
        tile._irIo.disconnect();
        tile._irIo = null;
      }
    });
  });
})();
